/**
 * Content Moderation System
 * Automatically detects and blocks suspicious messages to prevent fraud and scams
 */

export interface FlaggedContent {
  type: 'blocked' | 'warning' | 'suspicious'
  reason: string
  severity: 'high' | 'medium' | 'low'
  action: 'delete' | 'flag' | 'review'
}

export interface ModerationResult {
  isBlocked: boolean
  isFlagged: boolean
  warnings: string[]
  flaggedContent: FlaggedContent[]
  sanitizedMessage: string
}

export interface ModerationViolation {
  id?: string
  user_id: string
  application_id: string
  original_message: string
  violation_type: 'blocked' | 'warning' | 'suspicious'
  violation_reason: string
  severity: 'high' | 'medium' | 'low'
  created_at?: string
}

type Severity = 'high' | 'medium' | 'low'

// High-risk patterns that should immediately block messages
const BLOCKED_PATTERNS: { pattern: RegExp; reason: string; severity: Severity }[] = [
  // Contact information sharing - PRIMARY FOCUS
  { pattern: /\b(?:send|give|share)\s+(?:me|to\s+me)\s+(?:your|the)\s+(?:account\s+)?(?:number|details?)\b/i, reason: 'Requesting account number', severity: 'high' },
  { pattern: /\b(?:my|this|the)\s+(?:account\s+)?(?:number|details?)\s+is\s+(?:\d{8,}|\w+\d+\w+)\b/i, reason: 'Sharing account number', severity: 'high' },
  { pattern: /\b(?:send|give|share)\s+(?:me|to\s+me)\s+(?:your|the)\s+(?:phone\s+)?(?:number|contact)\b/i, reason: 'Requesting phone number', severity: 'high' },
  { pattern: /\b(?:my|this|the)\s+(?:phone\s+)?(?:number|contact)\s+is\s+(?:\+?\d{10,})\b/i, reason: 'Sharing phone number', severity: 'high' },
  { pattern: /\b(?:send|give|share)\s+(?:me|to\s+me)\s+(?:your|the)\s+(?:whatsapp|telegram|signal)\s+(?:number|contact)\b/i, reason: 'Requesting WhatsApp/messaging app contact', severity: 'high' },
  { pattern: /\b(?:my|this|the)\s+(?:whatsapp|telegram|signal)\s+(?:number|contact)\s+is\s+(?:\+?\d{10,})\b/i, reason: 'Sharing WhatsApp/messaging app contact', severity: 'high' },
  { pattern: /\b(?:contact|call|text|message)\s+(?:me|us)\s+(?:on|at|via)\s+(?:whatsapp|telegram|signal)\b/i, reason: 'Requesting external messaging app contact', severity: 'high' },
  { pattern: /\b(?:add|find)\s+(?:me|us)\s+(?:on|at)\s+(?:whatsapp|telegram|signal|facebook|instagram)\b/i, reason: 'Requesting social media contact', severity: 'high' },
  
  // Additional contact sharing patterns
  { pattern: /\b(?:call|text|whatsapp)\s+(?:me|us)\s+(?:at|on)\s+(?:\+?\d{10,})\b/i, reason: 'Sharing contact number for external communication', severity: 'high' },
  { pattern: /\b(?:here\s+is\s+my|my\s+number\s+is|you\s+can\s+reach\s+me\s+at)\s+(?:\+?\d{10,})\b/i, reason: 'Sharing personal contact information', severity: 'high' },
  { pattern: /\b(?:dm|message|text)\s+(?:me|us)\s+(?:your|the)\s+(?:number|contact|details?)\b/i, reason: 'Requesting contact information via DM', severity: 'high' },
  { pattern: /\b(?:let\'s\s+)?(?:move|take|continue)\s+(?:this|the\s+conversation)\s+(?:to|outside|off)\s+(?:whatsapp|telegram|phone|call)\b/i, reason: 'Attempting to move conversation off-platform', severity: 'high' },
  
  // Financial fraud patterns
  { pattern: /\b(?:send|transfer|pay|give)\s+(?:me|to\s+me)\s+(?:bank\s+)?(?:account\s+)?(?:details?|info)\b/i, reason: 'Requesting bank account details', severity: 'high' },
  { pattern: /\b(?:bank\s+)?(?:account\s+)?(?:number|details?|info)\s+(?:for|to)\s+(?:payment|transfer|deposit)\b/i, reason: 'Sharing bank account details', severity: 'high' },
  { pattern: /\b(?:send|transfer|pay)\s+(?:money|cash|funds?)\s+(?:to|at)\s+(?:my|this)\s+(?:account|number)\b/i, reason: 'Direct money transfer request', severity: 'high' },
  
  // Off-platform payment requests
  { pattern: /\b(?:pay|send|transfer)\s+(?:outside|off)\s+(?:the\s+)?(?:platform|app|website)\b/i, reason: 'Off-platform payment request', severity: 'high' },
  { pattern: /\b(?:direct\s+)?(?:bank\s+)?(?:transfer|payment)\s+(?:only|only)\b/i, reason: 'Direct bank transfer only', severity: 'high' },
  { pattern: /\b(?:avoid|skip|bypass)\s+(?:platform|app|website)\s+(?:fees?|charges?)\b/i, reason: 'Platform fee avoidance', severity: 'high' },
  
  // Urgent payment demands
  { pattern: /\b(?:urgent|immediate|asap|quick)\s+(?:payment|transfer|deposit)\b/i, reason: 'Urgent payment demand', severity: 'medium' },
  { pattern: /\b(?:pay\s+)?(?:now|immediately|urgently)\s+(?:or\s+)?(?:else|otherwise)\b/i, reason: 'Threatening payment demand', severity: 'high' },
  
  // Suspicious contact methods
  { pattern: /\b(?:contact|call|text|whatsapp)\s+(?:me\s+)?(?:at|on)\s+(?:\+?\d{10,}|\w+@\w+\.\w+)\b/i, reason: 'External contact information', severity: 'medium' },
  { pattern: /\b(?:meet|see)\s+(?:me|us)\s+(?:in\s+)?(?:person|outside|offline)\b/i, reason: 'Offline meeting request', severity: 'medium' },
  
  // Property verification scams
  { pattern: /\b(?:verify|confirm)\s+(?:property|house|apartment)\s+(?:ownership|title|deed)\b/i, reason: 'Property verification scam', severity: 'high' },
  { pattern: /\b(?:send|provide)\s+(?:documents?|papers?|proof)\s+(?:of|for)\s+(?:ownership|identity)\b/i, reason: 'Document request scam', severity: 'high' },
  
  // Rental agreement scams
  { pattern: /\b(?:sign|agree|accept)\s+(?:rental|lease)\s+(?:agreement|contract)\s+(?:first|before)\s+(?:seeing|viewing)\b/i, reason: 'Pre-viewing agreement scam', severity: 'high' },
  { pattern: /\b(?:pay|deposit)\s+(?:before|without)\s+(?:seeing|viewing|inspecting)\b/i, reason: 'Pre-viewing payment scam', severity: 'high' },
  
  // Suspicious discounts
  { pattern: /\b(?:special|exclusive|limited)\s+(?:discount|offer|deal)\s+(?:for\s+)?(?:you|direct|cash)\b/i, reason: 'Suspicious discount offer', severity: 'medium' },
  { pattern: /\b(?:cash\s+)?(?:discount|reduction)\s+(?:if\s+)?(?:pay\s+)?(?:now|immediately|today)\b/i, reason: 'Urgent cash discount', severity: 'medium' },
  
  // Personal information requests
  { pattern: /\b(?:send|provide|give)\s+(?:me|to\s+me)\s+(?:your|the)\s+(?:personal|private|sensitive)\s+(?:information|details?|data)\b/i, reason: 'Personal information request', severity: 'high' },
  { pattern: /\b(?:ssn|social\s+security|passport|drivers?\s+license|id\s+card)\s+(?:number|details?|copy)\b/i, reason: 'Government ID request', severity: 'high' },
  
  // Suspicious urgency
  { pattern: /\b(?:limited\s+)?(?:time|offer|availability)\s+(?:only|left|remaining)\b/i, reason: 'False urgency', severity: 'low' },
  { pattern: /\b(?:act\s+)?(?:fast|quick|now)\s+(?:before\s+)?(?:someone\s+)?(?:else|takes|gets)\b/i, reason: 'Competitive pressure', severity: 'low' }
]

// Warning patterns that should flag messages for review
const WARNING_PATTERNS: { pattern: RegExp; reason: string; severity: Severity }[] = [
  // Payment method preferences
  { pattern: /\b(?:prefer|like|want)\s+(?:cash|bank\s+transfer|direct\s+payment)\b/i, reason: 'Payment method preference', severity: 'low' },
  { pattern: /\b(?:avoid|don\'t\s+like|hate)\s+(?:credit\s+card|online\s+payment|platform)\b/i, reason: 'Payment method avoidance', severity: 'low' },
  
  // Meeting requests
  { pattern: /\b(?:meet|see|visit)\s+(?:the\s+)?(?:property|house|apartment)\b/i, reason: 'Property viewing request', severity: 'low' },
  { pattern: /\b(?:schedule|arrange|book)\s+(?:a\s+)?(?:meeting|appointment|viewing)\b/i, reason: 'Meeting scheduling', severity: 'low' },
  
  // Price negotiations
  { pattern: /\b(?:negotiate|discuss|talk\s+about)\s+(?:price|rent|cost|fee)\b/i, reason: 'Price negotiation', severity: 'low' },
  { pattern: /\b(?:lower|reduce|discount)\s+(?:price|rent|cost|fee)\b/i, reason: 'Price reduction request', severity: 'low' },
  
  // Property questions
  { pattern: /\b(?:ask|question|wonder|curious)\s+(?:about|regarding)\s+(?:the\s+)?(?:property|house|apartment)\b/i, reason: 'Property inquiry', severity: 'low' },
  { pattern: /\b(?:more\s+)?(?:information|details?|photos?|videos?)\s+(?:about|of|for)\b/i, reason: 'Information request', severity: 'low' }
]

// Suspicious words that should trigger review
const SUSPICIOUS_WORDS = [
  // Contact information
  'whatsapp', 'telegram', 'signal', 'phone number', 'contact number', 'mobile number',
  'account number', 'bank details', 'account details', 'routing number',
  'facebook', 'instagram', 'twitter', 'snapchat', 'discord',
  
  // Off-platform communication
  'call me', 'text me', 'message me', 'contact me', 'reach me',
  'outside the app', 'off platform', 'external contact',
  
  // Payment avoidance
  'urgent', 'immediate', 'asap', 'quick', 'fast', 'now',
  'special', 'exclusive', 'limited', 'secret', 'confidential',
  'avoid', 'bypass', 'skip', 'direct', 'offline', 'outside',
  'cash', 'bank transfer', 'direct payment',
  'personal', 'private', 'sensitive', 'verify', 'confirm',
  'documents', 'papers', 'proof', 'ownership', 'identity'
]

/**
 * Check if a message contains flagged content
 */
export function moderateMessage(message: string): ModerationResult {
  const warnings: string[] = []
  const flaggedContent: FlaggedContent[] = []
  let isBlocked = false
  let isFlagged = false
  
  // Check blocked patterns first
  for (const pattern of BLOCKED_PATTERNS) {
    if (pattern.pattern.test(message)) {
      flaggedContent.push({
        type: 'blocked',
        reason: pattern.reason,
        severity: pattern.severity,
        action: 'delete'
      })
      isBlocked = true
      warnings.push(`⚠️ Message blocked: ${pattern.reason}`)
    }
  }
  
  // Check warning patterns
  for (const pattern of WARNING_PATTERNS) {
    if (pattern.pattern.test(message)) {
      flaggedContent.push({
        type: 'warning',
        reason: pattern.reason,
        severity: pattern.severity,
        action: 'flag'
      })
      isFlagged = true
      warnings.push(`⚠️ Warning: ${pattern.reason}`)
    }
  }
  
  // Check for suspicious words
  const messageLower = message.toLowerCase()
  const foundSuspiciousWords = SUSPICIOUS_WORDS.filter(word => 
    messageLower.includes(word.toLowerCase())
  )
  
  if (foundSuspiciousWords.length > 0) {
    flaggedContent.push({
      type: 'suspicious',
      reason: `Contains suspicious words: ${foundSuspiciousWords.join(', ')}`,
      severity: 'medium',
      action: 'review'
    })
    isFlagged = true
    warnings.push(`⚠️ Suspicious content detected`)
  }
  
  // Sanitize message (remove sensitive information)
  let sanitizedMessage = message
  
  // Remove account numbers (8+ digits)
  sanitizedMessage = sanitizedMessage.replace(/\b\d{8,}\b/g, '[ACCOUNT_NUMBER]')
  
  // Remove email addresses
  sanitizedMessage = sanitizedMessage.replace(/\b\w+@\w+\.\w+\b/g, '[EMAIL]')
  
  // Remove phone numbers
  sanitizedMessage = sanitizedMessage.replace(/\b\+?\d{10,}\b/g, '[PHONE]')
  
  return {
    isBlocked,
    isFlagged,
    warnings,
    flaggedContent,
    sanitizedMessage
  }
}

/**
 * Get moderation statistics for admin dashboard
 */
export function getModerationStats() {
  return {
    blockedPatterns: BLOCKED_PATTERNS.length,
    warningPatterns: WARNING_PATTERNS.length,
    suspiciousWords: SUSPICIOUS_WORDS.length,
    totalPatterns: BLOCKED_PATTERNS.length + WARNING_PATTERNS.length
  }
}

/**
 * Get all flagged patterns for admin review
 */
export function getAllFlaggedPatterns() {
  return {
    blocked: BLOCKED_PATTERNS,
    warnings: WARNING_PATTERNS,
    suspicious: SUSPICIOUS_WORDS
  }
}

/**
 * Log moderation violation to database for admin review
 */
export async function logModerationViolation(violation: ModerationViolation): Promise<void> {
  try {
    // Best-effort logging to backend so admins can review violation trends.
    // We intentionally do NOT block the user on failures here.
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null
    if (!token) return

    const { getApiUrl } = await import('./apiUrl')
    const apiUrl = getApiUrl()

    await fetch(`${apiUrl}/moderation/violations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        applicationId: violation.application_id,
        originalMessage: violation.original_message,
        violationType: violation.violation_type,
        violationReason: violation.violation_reason,
        severity: violation.severity
      })
    }).catch(() => undefined)
  } catch (error) {
    console.error('Failed to log moderation violation:', error)
  }
}

/**
 * Enhanced moderation function that includes admin logging
 */
export async function moderateMessageWithLogging(
  message: string, 
  userId: string, 
  applicationId: string
): Promise<ModerationResult> {
  const result = moderateMessage(message)
  
  // Log violations for admin review
  if (result.isBlocked || result.isFlagged) {
    for (const flagged of result.flaggedContent) {
      await logModerationViolation({
        user_id: userId,
        application_id: applicationId,
        original_message: message,
        violation_type: flagged.type,
        violation_reason: flagged.reason,
        severity: flagged.severity
      })
    }
  }
  
  return result
}
